import { _getModelName, _getStore } from 'volta/utils/api/build-url';

import JSONSerializer from '@ember-data/serializer/json';
import { isArray } from '@ember/array';
import { typeOf } from '@ember/utils';

import type { Document as JSONApiDoc, DocWithData } from 'volta/utils/api/jsonapi-types';

import type {
  CollectionResourceDocument,
  SingleResourceDocument
} from '@warp-drive/core-types/spec/json-api-raw';
import type Model from '@ember-data/model';
import type { ObjectValue } from '@warp-drive/core-types/json/raw';

export function isJsonApi(raw: any): raw is JSONApiDoc {
  return raw.jsonapi && raw.jsonapi.version;
}

export function isDocWithData(doc: any): doc is DocWithData {
  return isJsonApi(doc) && ['object', 'array'].includes(typeOf((doc as DocWithData).data));
}

export type ModelOrCollection<M extends Model> = M | M[];
export type ModelOrCollectionWithMeta<M extends Model> = ModelOrCollection<M> & {
  meta?: ObjectValue;
};

export function normalize<M extends Model>(
  modelName: string,
  response: any
): ModelOrCollectionWithMeta<M> {
  if (!isDocWithData(response)) {
    // tslint:disable-next-line:no-console
    console.warn(
      '"normalize" may only be used with a JSON API document. Ignoring response. ' +
        'Document must have a mandatory JSON API object. See https://jsonapi.org/format/#document-jsonapi-object.'
    );
    return response;
  }
  const store = _getStore();
  const modelClass = store.modelFor(modelName);
  const serializer = store.serializerFor(modelName) as unknown as typeof JSONSerializer;

  if (isArray(response.data)) {
    const normalized = serializer.normalizeArrayResponse(
      store,
      modelClass,
      response,
      null as unknown as string,
      'query'
    ) as CollectionResourceDocument;
    const result = store.push(normalized) as M[] & { meta?: ObjectValue };

    result.meta = normalized.meta;
    return result;
  } else {
    const normalized: SingleResourceDocument = serializer.normalizeSingleResponse(
      store,
      modelClass,
      response,
      `${response.data.id ?? '(unknown)'}`,
      'findRecord'
    ) as SingleResourceDocument;
    return store.push(normalized) as M;
  }
}

export function _normalize<IN = any, M extends Model = Model>(this: typeof Model, response: IN) {
  const modelName = _getModelName(this);
  return normalize<M>(modelName, response);
}
